import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  QrCodeIcon,
  XMarkIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  DocumentDuplicateIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Pour la compatibilité avec les différentes méthodes d'accès à la webcam
const BrowserBarcodeReader = typeof window !== 'undefined' ? 
  require('@zxing/browser').BrowserMultiFormatReader : null;

/**
 * Composant de scanner de code-barres réutilisable
 * @param {Object} props
 * @param {Function} props.onScan - Fonction appelée quand un code-barres est détecté
 * @param {Function} props.onError - Fonction appelée en cas d'erreur
 * @param {boolean} props.isOpen - État d'ouverture du scanner
 * @param {Function} props.onClose - Fonction pour fermer le scanner
 * @param {string} props.mode - Mode d'utilisation : 'search' (recherche), 'add' (ajout) ou 'select' (sélection)
 * @param {string} props.title - Titre personnalisé pour le scanner
 * @param {boolean} props.continuous - Si true, continue à scanner après une détection
 */
const BarcodeScanner = ({ 
  onScan, 
  onError, 
  isOpen, 
  onClose,
  mode = 'search',
  title,
  continuous = false
}) => {
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [videoInputDevices, setVideoInputDevices] = useState([]);
  const [lastScannedCode, setLastScannedCode] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const videoRef = useRef(null);
  const codeReader = useRef(null);
  const videoContainerRef = useRef(null);
  
  const customTitle = title || (
    mode === 'search' ? 'Rechercher par code-barres' :
    mode === 'add' ? 'Scanner pour ajouter un produit' :
    'Scanner pour sélectionner un produit'
  );

  // Initialisation du scanner au montage du composant
  useEffect(() => {
    // Ne pas exécuter côté serveur
    if (typeof navigator === 'undefined') return;
    
    if (!codeReader.current) {
      codeReader.current = new BrowserBarcodeReader();
    }
    
    return () => {
      // Nettoyage lors du démontage
      if (codeReader.current) {
        try {
          codeReader.current.reset();
        } catch (error) {
          console.error('Erreur lors de la réinitialisation du scanner:', error);
        }
      }
    };
  }, []);

  // Gestion de l'ouverture/fermeture du scanner
  useEffect(() => {
    if (isOpen) {
      setupScanner();
    } else {
      stopScanning();
    }
  }, [isOpen]);

  // Configuration du scanner
  const setupScanner = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Votre navigateur ne prend pas en charge l\'accès à la caméra');
      }

      // Récupérer les périphériques d'entrée vidéo
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setVideoInputDevices(videoDevices);

      if (videoDevices.length === 0) {
        throw new Error('Aucune caméra détectée');
      }

      // Sélectionner la première caméra par défaut
      if (!selectedDeviceId && videoDevices.length > 0) {
        setSelectedDeviceId(videoDevices[0].deviceId);
        
        // Petite pause pour permettre le rendu de l'interface avant de démarrer la caméra
        setTimeout(() => {
          startScanning(videoDevices[0].deviceId);
        }, 500);
      } else if (selectedDeviceId) {
        startScanning(selectedDeviceId);
      }
      
      setScannerReady(true);
    } catch (error) {
      setScannerReady(false);
      handleError(error);
    }
  };

  // Démarrer le scanning
  const startScanning = async (deviceId) => {
    try {
      setScanning(true);
      setLastScannedCode('');
      
      if (!codeReader.current || !videoRef.current) {
        throw new Error('Scanner non initialisé');
      }
      
      // Réinitialiser avant de démarrer
      codeReader.current.reset();
      
      // Démarrer le scanner avec le périphérique sélectionné
      await codeReader.current.decodeFromVideoDevice(
        deviceId,
        videoRef.current,
        (result, error) => {
          if (result) {
            handleScan(result.getText());
          }
          if (error && !(error instanceof TypeError)) {
            // Ignorer les TypeError qui se produisent souvent lors du nettoyage
            console.error('Erreur de scan:', error);
          }
        }
      );
      
      toast.success('Scanner activé');
    } catch (error) {
      setScanning(false);
      handleError(error);
    }
  };

  // Arrêter le scanning
  const stopScanning = () => {
    if (codeReader.current) {
      try {
        codeReader.current.reset();
        setScanning(false);
      } catch (error) {
        console.error('Erreur lors de l\'arrêt du scanner:', error);
      }
    }
  };

  // Changer de caméra
  const changeCamera = (deviceId) => {
    stopScanning();
    setSelectedDeviceId(deviceId);
    startScanning(deviceId);
  };

  // Gérer le scan d'un code-barres
  const handleScan = (code) => {
    if (!code || code === lastScannedCode) return;
    
    setLastScannedCode(code);
    
    // Jouer un son de réussite
    const audio = new Audio('/assets/audio/beep.mp3');
    audio.play().catch(e => console.log('Audio play failed:', e));
    
    // Afficher un toast
    toast.success(`Code détecté: ${code}`);
    
    // Appeler le callback
    onScan && onScan(code);
    
    // Si le mode n'est pas continu, arrêter le scanning
    if (!continuous) {
      stopScanning();
      onClose && onClose();
    }
  };

  // Gérer les erreurs
  const handleError = (error) => {
    console.error('Erreur du scanner:', error);
    const errorMessage = error.message || 'Erreur lors de l\'accès à la caméra';
    toast.error(errorMessage);
    onError && onError(errorMessage);
  };

  // Soumission du code manuel
  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      handleScan(manualCode.trim());
      setManualCode('');
    }
  };

  // Copier le dernier code dans le presse-papier
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code).then(
      () => toast.success('Code copié !'),
      () => toast.error('Impossible de copier le code')
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div 
            className="w-full max-w-lg rounded-3xl overflow-hidden bg-slate-900 border border-white/20 shadow-xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            {/* En-tête */}
            <div className="relative flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white">
              <QrCodeIcon className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-semibold">{customTitle}</h3>
              <button 
                className="absolute right-4 text-white hover:text-white/80"
                onClick={onClose}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* Contenu */}
            <div className="p-4">
              {/* Zone vidéo */}
              <div 
                ref={videoContainerRef} 
                className="relative bg-black rounded-lg overflow-hidden aspect-[4/3] mb-4"
              >
                {scannerReady ? (
                  <video 
                    ref={videoRef} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                    <QrCodeIcon className="h-16 w-16 mb-2 text-white/30" />
                    <p className="text-center text-white/70">Initialisation du scanner...</p>
                  </div>
                )}
                
                {/* Overlay de scan */}
                {scanning && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 border-2 border-blue-500/50 rounded-lg"></div>
                    <div className="absolute left-1/2 top-0 w-0.5 h-full bg-red-500/50 transform -translate-x-1/2 animate-scanner-line"></div>
                  </div>
                )}
                
                {/* Dernier code scanné */}
                {lastScannedCode && (
                  <div className="absolute bottom-4 left-4 right-4 bg-black/80 text-white p-2 rounded-lg flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-400" />
                      <span className="truncate">{lastScannedCode}</span>
                    </div>
                    <button 
                      className="text-blue-400 hover:text-blue-300"
                      onClick={() => copyToClipboard(lastScannedCode)}
                    >
                      <DocumentDuplicateIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Sélection de la caméra */}
              {videoInputDevices.length > 1 && (
                <div className="mb-4">
                  <label htmlFor="cameraSelect" className="block text-white/80 text-sm mb-1">
                    Sélectionner une caméra:
                  </label>
                  <select
                    id="cameraSelect"
                    value={selectedDeviceId}
                    onChange={(e) => changeCamera(e.target.value)}
                    className="w-full p-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  >
                    {videoInputDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Caméra ${videoInputDevices.indexOf(device) + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Saisie manuelle */}
              <form onSubmit={handleManualSubmit} className="mb-4">
                <label htmlFor="manualCode" className="block text-white/80 text-sm mb-1">
                  Saisie manuelle du code:
                </label>
                <div className="flex space-x-2">
                  <input
                    id="manualCode"
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    className="flex-1 p-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Entrez un code manuellement"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    OK
                  </button>
                </div>
              </form>
              
              {/* Note d'information */}
              <div className="flex items-start space-x-2 bg-blue-500/10 text-blue-300 p-3 rounded-lg text-sm">
                <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <p>
                  {mode === 'search' 
                    ? 'Scannez un code-barres pour rechercher un produit dans votre catalogue.'
                    : mode === 'add' 
                    ? 'Scannez un code-barres pour ajouter rapidement un produit au formulaire.'
                    : 'Scannez un code-barres pour sélectionner un produit.'
                  }
                </p>
              </div>
              
              {/* Boutons d'action */}
              <div className="flex justify-end mt-4 space-x-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  Annuler
                </button>
                
                {mode === 'add' && lastScannedCode && (
                  <button
                    onClick={() => onScan && onScan(lastScannedCode, true)}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg flex items-center space-x-2"
                  >
                    <PlusCircleIcon className="h-5 w-5" />
                    <span>Ajouter produit</span>
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BarcodeScanner; 